import { Box, Button, Stack, Typography } from '@mui/material';
import { type ComponentProps, useEffect } from 'react';
import { useOutletContext } from 'react-router';

import { type Breadcrumb, Breadcrumbs } from './Breadcrumbs';
import type { PrivateRouteContext } from './PrivateRoute';

export interface PageAction {
    key: string;
    label: string;
    variant?: ComponentProps<typeof Button>['variant'];
    onClick: () => void;
}

export enum ContentWidth {
    PADDED_SMALL = 'padded-small',
    FULL = 'full',
}

interface Props {
    breadcrumbs?: Breadcrumb[];
    title?: string;
    children: React.ReactNode;
    actions?: PageAction[];
    navigation?: React.ReactNode;
    contentWidth?: ContentWidth;
}

export const Page = ({
    actions,
    breadcrumbs,
    children,
    contentWidth = ContentWidth.PADDED_SMALL,
    navigation,
    title,
}: Props) => {
    const { setNavComponent } = useOutletContext<PrivateRouteContext>() ?? {};
    useEffect(() => {
        setNavComponent?.(navigation);
    }, [navigation, setNavComponent]);

    const headerHeight = 66;
    const widthMargin = contentWidth === ContentWidth.PADDED_SMALL ? 15 : 0;
    const margin = contentWidth === ContentWidth.PADDED_SMALL ? '15px' : 0;

    return (
        <Stack
            sx={{
                width: '100%',
                height: `calc(100vh - ${headerHeight}px)`,
                marginTop: `${headerHeight}px`,
                overflow: 'auto',
                flexDirection: 'column',
                overflowX: 'hidden',
            }}
        >
            {title && breadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} title={title} />}
            <Stack
                sx={{
                    width: `calc(100% - ${widthMargin * 2}px)`,
                    marginLeft: margin,
                    marginRight: margin,
                    flexGrow: 1,
                }}
            >
                {(title || actions) && (
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                            paddingBottom: 4,
                        }}
                    >
                        {title && (
                            <Typography
                                variant="h1"
                                component="h1"
                                id="page-title"
                                sx={{
                                    color: 'inherit',
                                    fontSize: '2rem',
                                    paddingBottom: 2,
                                    paddingTop: 2,
                                }}
                            >
                                {title}
                            </Typography>
                        )}
                        <Box
                            id="page-actions"
                            sx={{
                                flex: 'row',
                                paddingTop: 2,
                            }}
                        >
                            {(actions ?? []).map(({ key, label, onClick, variant }) => (
                                <Box
                                    key={key}
                                    sx={{
                                        paddingLeft: 1,
                                        display: 'inline-block',
                                    }}
                                >
                                    <Button variant={variant ?? 'contained'} color="primary" onClick={onClick}>
                                        {label}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                )}
                {/* Use some nested boxes to force a reset of height 100% after taking full height through the stack
                with flex grow */}
                <Box
                    sx={{
                        position: 'relative',
                        flexGrow: 1,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Stack>
        </Stack>
    );
};
